import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

interface Outreach {
  id: number;
  handle: string;
  nome: string;
  categoria: string;
  tipo_alvo: string;
  regiao: string | null;
  status: string;
  prioridade: number;
  handle_confirmado: boolean;
  fonte: string | null;
  seguido_em: string | null;
  total_interacoes: number;
  ultima_interacao: string | null;
}

interface Interacao {
  id: number;
  outreach_id: number;
  ocorrido_em: string;
  texto: string;
  created_at: string;
}

interface Stats {
  total: number;
  a_seguir: number;
  seguido: number;
  engajado: number;
  retornaram: number;
  parcerias: number;
  alcancados: number;
  taxa_retorno: number;
}

const CATEGORIAS: { key: string; label: string; cor: string }[] = [
  { key: 'banda_mainstream', label: 'Banda (mainstream)', cor: '#E63950' },
  { key: 'banda_underground', label: 'Banda (underground)', cor: '#b5179e' },
  { key: 'estudio', label: 'Estúdio', cor: '#3a86ff' },
  { key: 'instrumentos', label: 'Instrumentos', cor: '#0096c7' },
  { key: 'festival_produtora', label: 'Festival/Produtora', cor: '#ff6600' },
  { key: 'casa_show', label: 'Casa de show', cor: '#fb8500' },
  { key: 'gravadora_selo', label: 'Gravadora/Selo', cor: '#8338ec' },
  { key: 'midia_radio', label: 'Mídia/Rádio', cor: '#2a9d8f' },
  { key: 'roupa', label: 'Roupa/Loja', cor: '#6c757d' },
];

const STATUS: { key: string; label: string; cor: string }[] = [
  { key: 'a_seguir', label: 'A seguir', cor: '#6c757d' },
  { key: 'ja_nos_segue', label: 'Já nos segue (pular)', cor: '#adb5bd' },
  { key: 'seguido', label: 'Seguido', cor: '#3a86ff' },
  { key: 'engajado', label: 'Engajei', cor: '#0096c7' },
  { key: 'respondeu', label: 'Respondeu', cor: '#2a9d8f' },
  { key: 'parceria', label: 'Parceria', cor: '#28a745' },
  { key: 'sem_retorno', label: 'Sem retorno', cor: '#dc3545' },
];

const TIPOS: { key: string; label: string }[] = [
  { key: 'parceria', label: 'Parceria (B2B)' },
  { key: 'seguir_de_volta', label: 'Seguir de volta' },
];

const catInfo = (k: string) => CATEGORIAS.find(c => c.key === k);
const statusInfo = (k: string) => STATUS.find(s => s.key === k);
const fmtData = (s: string | null) => (s ? new Date(s).toLocaleDateString('pt-BR') : '—');
const fmtDataHora = (s: string) => new Date(s).toLocaleString('pt-BR');

const SocialOutreach: React.FC = () => {
  const [rows, setRows] = useState<Outreach[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [fStatus, setFStatus] = useState('a_seguir');
  const [fCategoria, setFCategoria] = useState('');
  const [fTipo, setFTipo] = useState('');
  const [busca, setBusca] = useState('');
  const [proximaLeva, setProximaLeva] = useState(false);

  // Modal timeline
  const [timelineDe, setTimelineDe] = useState<Outreach | null>(null);
  const [interacoes, setInteracoes] = useState<Interacao[]>([]);
  const [novaNota, setNovaNota] = useState('');
  const [novaData, setNovaData] = useState('');

  // Modal adicionar
  const [addAberto, setAddAberto] = useState(false);
  const [novo, setNovo] = useState({ handle: '', nome: '', categoria: 'banda_underground', tipo_alvo: 'seguir_de_volta', regiao: '', nota: '' });

  const fetchStats = useCallback(async () => {
    try {
      const r = await api.get('/admin/social/outreach/stats');
      setStats(r.data);
    } catch (e) { console.error('Erro stats outreach', e); }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fStatus) params.append('status', fStatus);
      if (fCategoria) params.append('categoria', fCategoria);
      if (fTipo) params.append('tipo', fTipo);
      if (busca.trim()) params.append('q', busca.trim());
      const r = await api.get(`/admin/social/outreach?${params.toString()}`);
      setRows(r.data);
    } catch (e) {
      console.error('Erro listar outreach', e);
    } finally {
      setLoading(false);
    }
  }, [fStatus, fCategoria, fTipo, busca]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => {
    const t = setTimeout(fetchRows, busca ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchRows, busca]);

  // "Próxima leva": 20 'a_seguir' intercalando categorias (variedade na rodada)
  const linhasExibidas = useMemo(() => {
    if (!proximaLeva) return rows;
    const aSeguir = rows.filter(r => r.status === 'a_seguir');
    const porCat: Record<string, Outreach[]> = {};
    aSeguir.forEach(r => { (porCat[r.categoria] = porCat[r.categoria] || []).push(r); });
    const buckets = Object.values(porCat);
    const ordenado: Outreach[] = [];
    let i = 0;
    while (ordenado.length < aSeguir.length) {
      const b = buckets[i % buckets.length];
      if (b.length) ordenado.push(b.shift()!);
      i++;
      if (buckets.every(x => x.length === 0)) break;
    }
    return ordenado.slice(0, 20);
  }, [rows, proximaLeva]);

  const mudarStatus = async (row: Outreach, status: string) => {
    try {
      await api.put(`/admin/social/outreach/${row.id}`, { status });
      await Promise.all([fetchRows(), fetchStats()]);
    } catch (e) { alert('Erro ao mudar status.'); }
  };

  const marcarSeguido = (row: Outreach) => mudarStatus(row, 'seguido');

  const remover = async (row: Outreach) => {
    if (!window.confirm(`Remover @${row.handle} da lista?`)) return;
    try {
      await api.delete(`/admin/social/outreach/${row.id}`);
      await Promise.all([fetchRows(), fetchStats()]);
    } catch (e) { alert('Erro ao remover.'); }
  };

  const abrirTimeline = async (row: Outreach) => {
    setTimelineDe(row);
    setInteracoes([]);
    setNovaNota('');
    setNovaData('');
    try {
      const r = await api.get(`/admin/social/outreach/${row.id}/interacoes`);
      setInteracoes(r.data);
    } catch (e) { console.error('Erro timeline', e); }
  };

  const addNota = async () => {
    if (!timelineDe || !novaNota.trim()) return;
    try {
      await api.post(`/admin/social/outreach/${timelineDe.id}/interacoes`, {
        texto: novaNota.trim(),
        ocorrido_em: novaData || undefined,
      });
      const r = await api.get(`/admin/social/outreach/${timelineDe.id}/interacoes`);
      setInteracoes(r.data);
      setNovaNota('');
      setNovaData('');
      fetchRows();
    } catch (e) { alert('Erro ao adicionar nota.'); }
  };

  const removerNota = async (iid: number) => {
    if (!timelineDe) return;
    if (!window.confirm('Apagar esta nota?')) return;
    try {
      await api.delete(`/admin/social/outreach/${timelineDe.id}/interacoes/${iid}`);
      setInteracoes(prev => prev.filter(i => i.id !== iid));
      fetchRows();
    } catch (e) { alert('Erro ao apagar nota.'); }
  };

  const criar = async () => {
    if (!novo.handle.trim() || !novo.nome.trim()) { alert('Informe @ e nome.'); return; }
    try {
      await api.post('/admin/social/outreach', novo);
      setAddAberto(false);
      setNovo({ handle: '', nome: '', categoria: 'banda_underground', tipo_alvo: 'seguir_de_volta', regiao: '', nota: '' });
      await Promise.all([fetchRows(), fetchStats()]);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Erro ao criar perfil.');
    }
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.titulo}>Networking IG — @tribhusbr</h1>
      <p style={styles.subtitulo}>
        Lista curada de perfis do ecossistema rock pra seguir e engajar. Objetivo: puxar gente nova.
        Ao abrir o perfil, se aparecer <b>"Segue você"</b> → marque <b>Já nos segue (pular)</b>. Em parceria, curta/comente antes de seguir.
      </p>

      {/* Cards de estatística */}
      {stats && (
        <div style={styles.statsRow}>
          <StatCard label="Total" valor={stats.total} cor="#212529" />
          <StatCard label="A seguir" valor={stats.a_seguir} cor="#6c757d" />
          <StatCard label="Seguidos" valor={stats.seguido} cor="#3a86ff" />
          <StatCard label="Engajei" valor={stats.engajado} cor="#0096c7" />
          <StatCard label="Responderam" valor={stats.retornaram} cor="#2a9d8f" />
          <StatCard label="Parcerias" valor={stats.parcerias} cor="#28a745" />
          <StatCard label="% retorno" valor={`${stats.taxa_retorno}%`} cor="#ff6600" />
        </div>
      )}

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <button
          onClick={() => setProximaLeva(p => !p)}
          style={{ ...styles.btnLeva, ...(proximaLeva ? styles.btnLevaAtivo : {}) }}
        >
          {proximaLeva ? '✓ Próxima leva (20)' : 'Próxima leva (20)'}
        </button>
        <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={styles.select} disabled={proximaLeva}>
          <option value="">Todos status</option>
          {STATUS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select value={fCategoria} onChange={e => setFCategoria(e.target.value)} style={styles.select}>
          <option value="">Todas categorias</option>
          {CATEGORIAS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select value={fTipo} onChange={e => setFTipo(e.target.value)} style={styles.select}>
          <option value="">Parceria + Seguir</option>
          {TIPOS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <input
          placeholder="Buscar @ ou nome..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          style={styles.input}
        />
        <button onClick={() => setAddAberto(true)} style={styles.btnAdd}>+ Adicionar perfil</button>
      </div>

      {loading && <p>Carregando...</p>}
      {!loading && !linhasExibidas.length && <p style={{ color: '#999' }}>Nenhum perfil com esses filtros.</p>}

      {/* Tabela */}
      {!!linhasExibidas.length && (
        <div style={styles.tabelaWrap}>
          <table style={styles.tabela}>
            <thead>
              <tr>
                <th style={styles.th}>Perfil</th>
                <th style={styles.th}>Categoria</th>
                <th style={styles.th}>Tipo</th>
                <th style={styles.th}>Região</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Timeline</th>
                <th style={styles.th}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {linhasExibidas.map(row => {
                const cat = catInfo(row.categoria);
                return (
                  <tr key={row.id} style={styles.tr}>
                    <td style={styles.td}>
                      <a href={`https://instagram.com/${row.handle}`} target="_blank" rel="noreferrer" style={styles.handle}>
                        @{row.handle}
                      </a>
                      {!row.handle_confirmado && <span title="Grafia do @ a confirmar" style={styles.aviso}>⚠️</span>}
                      <div style={styles.nomeSec}>{row.nome}</div>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: cat?.cor || '#888' }}>{cat?.label || row.categoria}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badgeTipo, ...(row.tipo_alvo === 'parceria' ? styles.tipoParceria : {}) }}>
                        {row.tipo_alvo === 'parceria' ? 'Parceria' : 'Seguir'}
                      </span>
                    </td>
                    <td style={styles.td}>{row.regiao || '—'}</td>
                    <td style={styles.td}>
                      <select
                        value={row.status}
                        onChange={e => mudarStatus(row, e.target.value)}
                        style={{ ...styles.statusSelect, color: statusInfo(row.status)?.cor }}
                      >
                        {STATUS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                    </td>
                    <td style={styles.td}>
                      <button onClick={() => abrirTimeline(row)} style={styles.btnTimeline}>
                        🗒 {row.total_interacoes}
                      </button>
                      <div style={styles.ultima}>{row.ultima_interacao ? fmtData(row.ultima_interacao) : ''}</div>
                    </td>
                    <td style={styles.td}>
                      {row.status === 'a_seguir' && (
                        <button onClick={() => marcarSeguido(row)} style={styles.btnSeguido}>Marcar seguido</button>
                      )}
                      <button onClick={() => remover(row)} style={styles.btnDel} title="Remover">✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Timeline */}
      {timelineDe && (
        <div style={styles.overlay} onClick={() => setTimelineDe(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHead}>
              <h3 style={{ margin: 0 }}>
                <a href={`https://instagram.com/${timelineDe.handle}`} target="_blank" rel="noreferrer" style={styles.handle}>
                  @{timelineDe.handle}
                </a>
              </h3>
              <button onClick={() => setTimelineDe(null)} style={styles.btnFechar}>✕</button>
            </div>
            <p style={{ color: '#666', marginTop: 4 }}>{timelineDe.nome}</p>

            <div style={styles.addNotaBox}>
              <textarea
                placeholder="Nova nota (ex: 'mandei DM se topam parceria de divulgação')"
                value={novaNota}
                onChange={e => setNovaNota(e.target.value)}
                style={styles.textarea}
              />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="datetime-local" value={novaData} onChange={e => setNovaData(e.target.value)} style={styles.inputData} />
                <button onClick={addNota} style={styles.btnAdd}>Adicionar nota</button>
              </div>
            </div>

            <div style={styles.timeline}>
              {!interacoes.length && <p style={{ color: '#999' }}>Sem interações ainda.</p>}
              {interacoes.map(i => (
                <div key={i.id} style={styles.notaItem}>
                  <div style={styles.notaData}>{fmtDataHora(i.ocorrido_em)}</div>
                  <div style={styles.notaTexto}>{i.texto}</div>
                  <button onClick={() => removerNota(i.id)} style={styles.btnNotaDel} title="Apagar">✕</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal Adicionar */}
      {addAberto && (
        <div style={styles.overlay} onClick={() => setAddAberto(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHead}>
              <h3 style={{ margin: 0 }}>Adicionar perfil</h3>
              <button onClick={() => setAddAberto(false)} style={styles.btnFechar}>✕</button>
            </div>
            <div style={styles.formGrid}>
              <label style={styles.formLabel}>@ (sem o @)
                <input value={novo.handle} onChange={e => setNovo({ ...novo, handle: e.target.value })} style={styles.input} />
              </label>
              <label style={styles.formLabel}>Nome / descrição
                <input value={novo.nome} onChange={e => setNovo({ ...novo, nome: e.target.value })} style={styles.input} />
              </label>
              <label style={styles.formLabel}>Categoria
                <select value={novo.categoria} onChange={e => setNovo({ ...novo, categoria: e.target.value })} style={styles.select}>
                  {CATEGORIAS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </label>
              <label style={styles.formLabel}>Tipo
                <select value={novo.tipo_alvo} onChange={e => setNovo({ ...novo, tipo_alvo: e.target.value })} style={styles.select}>
                  {TIPOS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
              <label style={styles.formLabel}>Região (opcional)
                <input value={novo.regiao} onChange={e => setNovo({ ...novo, regiao: e.target.value })} style={styles.input} />
              </label>
              <label style={styles.formLabel}>Nota inicial (opcional)
                <input value={novo.nota} onChange={e => setNovo({ ...novo, nota: e.target.value })} style={styles.input} />
              </label>
            </div>
            <button onClick={criar} style={{ ...styles.btnAdd, marginTop: 12 }}>Salvar perfil</button>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{ label: string; valor: number | string; cor: string }> = ({ label, valor, cor }) => (
  <div style={styles.statCard}>
    <div style={{ ...styles.statValor, color: cor }}>{valor}</div>
    <div style={styles.statLabel}>{label}</div>
  </div>
);

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: 0 },
  titulo: { marginBottom: 4, color: '#fff' },
  subtitulo: { color: '#b0b0b0', marginTop: 0, maxWidth: 800, fontSize: 14 },
  statsRow: { display: 'flex', gap: 12, flexWrap: 'wrap', margin: '16px 0' },
  statCard: { background: '#fff', borderRadius: 10, padding: '12px 18px', minWidth: 90, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' },
  statValor: { fontSize: 26, fontWeight: 'bold' },
  statLabel: { fontSize: 12, color: '#666', marginTop: 2 },
  toolbar: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 },
  select: { padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', background: '#fff' },
  input: { padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', minWidth: 180 },
  inputData: { padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc' },
  btnLeva: { padding: '8px 16px', borderRadius: 20, border: '1px solid #ff6600', background: '#fff', color: '#ff6600', cursor: 'pointer', fontWeight: 'bold' },
  btnLevaAtivo: { background: '#ff6600', color: '#fff' },
  btnAdd: { padding: '8px 16px', borderRadius: 6, border: 'none', background: '#28a745', color: '#fff', cursor: 'pointer', fontWeight: 'bold' },
  tabelaWrap: { background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.2)' },
  tabela: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: { textAlign: 'left', padding: '12px 14px', background: '#f1f3f5', color: '#333', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 },
  tr: { borderBottom: '1px solid #eee' },
  td: { padding: '10px 14px', verticalAlign: 'top' },
  handle: { color: '#3a86ff', fontWeight: 'bold', textDecoration: 'none' },
  aviso: { marginLeft: 6 },
  nomeSec: { color: '#666', fontSize: 12, marginTop: 2, maxWidth: 260 },
  badge: { color: '#fff', borderRadius: 12, padding: '2px 10px', fontSize: 11, fontWeight: 'bold', whiteSpace: 'nowrap' },
  badgeTipo: { border: '1px solid #adb5bd', color: '#666', borderRadius: 12, padding: '2px 10px', fontSize: 11 },
  tipoParceria: { borderColor: '#8338ec', color: '#8338ec', fontWeight: 'bold' },
  statusSelect: { padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc', fontWeight: 'bold', cursor: 'pointer', background: '#fff' },
  btnTimeline: { padding: '4px 10px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' },
  ultima: { fontSize: 11, color: '#999', marginTop: 2 },
  btnSeguido: { padding: '6px 10px', borderRadius: 6, border: 'none', background: '#3a86ff', color: '#fff', cursor: 'pointer', marginRight: 6, fontSize: 12 },
  btnDel: { padding: '6px 9px', borderRadius: 6, border: '1px solid #dc3545', background: '#fff', color: '#dc3545', cursor: 'pointer' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal: { background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 560, maxHeight: '85vh', overflowY: 'auto' },
  modalHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  btnFechar: { background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: '#666' },
  addNotaBox: { background: '#f8f9fa', borderRadius: 8, padding: 12, margin: '12px 0' },
  textarea: { width: '100%', minHeight: 60, padding: 8, borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box', marginBottom: 8, resize: 'vertical' },
  timeline: { marginTop: 8 },
  notaItem: { borderLeft: '3px solid #ff6600', padding: '6px 10px', marginBottom: 8, background: '#fafafa', borderRadius: 4, position: 'relative' },
  notaData: { fontSize: 11, color: '#999' },
  notaTexto: { fontSize: 14, color: '#333', whiteSpace: 'pre-wrap', paddingRight: 20 },
  btnNotaDel: { position: 'absolute', top: 6, right: 6, background: 'transparent', border: 'none', color: '#bbb', cursor: 'pointer' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 },
  formLabel: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#333' },
};

export default SocialOutreach;
